import { HttpClient, HttpEvent, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataImport, DataImportStatus, SearchParams } from '../../model';
import { AccountingCode, AccountingCodeUserInfo, AccountingCodesSearchResult } from '../../model/accounting-code.model';

@Injectable({
  providedIn: 'root',
})
export class AccountingCodesAdminConnector {
  constructor(private httpClient: HttpClient, private occEndpointsService: OccEndpointsService) {}

  search(userId: string, searchParams: SearchParams): Observable<AccountingCodesSearchResult> {
    return this.httpClient.get<AccountingCodesSearchResult>(
      this.occEndpointsService.buildUrl('accountingCodesAdmin', {
        urlParams: { userId: userId },
        queryParams: {
          fields: 'FULL',
          page: searchParams?.page ?? 0,
          count: searchParams?.count ?? 10,
        },
      })
    );
  }

  create(userId: string, accountingCode: AccountingCode): Observable<AccountingCode> {
    return this.httpClient.post(
      this.occEndpointsService.buildUrl('accountingCodesAdmin', {
        urlParams: {
          userId: userId,
        },
      }),
      accountingCode
    );
  }

  update(userId: string, accountingCode: AccountingCode): Observable<AccountingCode> {
    return this.httpClient.patch(
      this.occEndpointsService.buildUrl('accountingCodeAdmin', {
        urlParams: {
          userId,
          code: accountingCode.code,
        },
        queryParams: { description: accountingCode.description },
      }),
      accountingCode
    );
  }

  delete(userId: string, code: string): Observable<any> {
    return this.httpClient.delete(
      this.occEndpointsService.buildUrl('accountingCodeAdmin', {
        urlParams: {
          userId,
          code,
        },
      })
    );
  }

  export(userId: string): Observable<Blob> {
    return this.httpClient.get(
      this.occEndpointsService.buildUrl('accountingCodesExportXlsAdmin', {
        urlParams: {
          userId,
        },
      }),
      {
        responseType: 'blob',
      }
    );
  }

  import(userId: string, file: File): Observable<number> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    const progress = new BehaviorSubject<number>(0);

    const req = new HttpRequest(
      'POST',
      this.occEndpointsService.buildUrl('accountingCodesImportXlsAdmin', {
        urlParams: {
          userId,
        },
      }),
      formData,
      {
        reportProgress: true,
      }
    );

    this.httpClient.request(req).subscribe(
      (event: HttpEvent<any> | any) => {
        if (event.type === HttpEventType.UploadProgress) {
          progress.next(Math.round((100 * event.loaded) / event.total));
        } else if (event instanceof HttpResponse) {
          const dataImportResult = event.body as DataImport;
          if (
            dataImportResult?.status === DataImportStatus.FAILED ||
            dataImportResult?.status === DataImportStatus.IMPORTED_WITH_ERRORS
          ) {
            progress.error(dataImportResult);
          }
          if (dataImportResult?.status === DataImportStatus.IMPORTED) {
            progress.next(100);
            progress.complete();
          }
        }
      },
      (error) => progress.error(error)
    );
    return progress.asObservable();
  }

  load(userId: string): Observable<AccountingCodeUserInfo> {
    return this.httpClient.get<AccountingCodeUserInfo>(
      this.occEndpointsService.buildUrl('accountingCodeUserInfo', {
        urlParams: { userId: userId },
      })
    );
  }
}
